import React, { useEffect } from "react";
import { createPortal } from "react-dom";

interface PanelProps {
	title: string;
	text: string;
	buttonText: string;
	mobileIcon: React.ReactNode;
	closeButtonAriaLabel: string;
	onClickCloseButton: () => void;
	onClickMainButton: () => void;
}

interface WrapperProps {
	visible: boolean;
	className?: string;
	children: React.ReactNode;
}

const Icon = {
	failure: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={49}
			height={48}
			viewBox="0 0 49 48"
			fill="none"
		>
			<title>Failure</title>
			<g clipPath="url(#clip0_894_31226)">
				<path
					d="M38.3516 10L10.3516 38M38.3516 38L10.3516 10"
					stroke="#BB320C"
					strokeWidth="1.5"
					strokeMiterlimit={10}
					strokeLinecap="square"
				/>
			</g>
			<defs>
				<clipPath id="clip0_894_31226">
					<rect
						width={48}
						height={48}
						fill="white"
						transform="translate(0.351562)"
					/>
				</clipPath>
			</defs>
		</svg>
	),

	success: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={48}
			height={48}
			viewBox="0 0 48 48"
			fill="none"
		>
			<title>Success</title>
			<g clipPath="url(#clip0_894_28080)">
				<path
					d="M3.75 24.75L17.25 38.25L45.75 9.75"
					stroke="#217331"
					strokeWidth="1.5"
					strokeMiterlimit={10}
					strokeLinecap="square"
				/>
			</g>
			<defs>
				<clipPath id="clip0_894_28080">
					<rect width={48} height={48} fill="white" />
				</clipPath>
			</defs>
		</svg>
	),
};

const Panel = ({
	title,
	text,
	buttonText,
	mobileIcon,
	closeButtonAriaLabel,
	onClickCloseButton,
	onClickMainButton,
}: PanelProps) => (
	<div
		role="dialog"
		aria-modal="true"
		aria-labelledby="mypages-modal-dialog-title"
		aria-describedby="mypages-modal-dialog-body"
		className="if color background lighter-beige mypages-modal-dialog__window"
	>
		<button
			type="button"
			className="mypages-modal-dialog__close-button"
			onClick={onClickCloseButton}
			aria-label={closeButtonAriaLabel}
		>
			<span
				aria-hidden="true"
				className="if icon ui close mypages-modal-dialog__close-button-icon"
			/>
		</button>

		<div className="mypages-modal-dialog__inner-content">
			<div className="mypages-modal-dialog__inner-content-top">
				<div className="mypages-modal-dialog__mobile-only-status-icon">
					{mobileIcon}
				</div>

				<div
					id="mypages-modal-dialog-title"
					className="if heading medium mypages-modal-dialog__header-text"
				>
					{title}
				</div>

				<div
					id="mypages-modal-dialog-body"
					className="if text body mypages-modal-dialog__body-text"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			</div>

			<div className="mypages-modal-dialog__inner-content-bottom">
				<button
					type="button"
					className="if button primary mypages-modal-dialog__action-button"
					onClick={onClickMainButton}
				>
					{buttonText}
				</button>
			</div>
		</div>
	</div>
);

const Wrapper = ({ visible, className = "", children }: WrapperProps) => {
	useEffect(() => {
		Array.from(document.body.children)
			.filter((el) => !el.classList.contains("mypages-modal-dialog"))
			.forEach((el) => {
				if (visible) {
					el.setAttribute("inert", "true");
				} else {
					el.removeAttribute("inert");
				}
			});

		if (visible) {
			document.body.classList.add("mypages-modal-dialog__body--modal-open");
		} else {
			document.body.classList.remove("mypages-modal-dialog__body--modal-open");
		}

		return () => {
			Array.from(document.body.children).forEach((el) => {
				el.removeAttribute("inert");
			});
			document.body.classList.remove("mypages-modal-dialog__body--modal-open");
		};
	}, [visible]);

	if (!visible) {
		return null;
	}

	return createPortal(
		<div className={`mypages-modal-dialog ${className}`}>{children}</div>,
		document.body,
	);
};

const ModalDialog = {
	Panel,
	Icon,
	Wrapper,
};

export default ModalDialog;
